import { useRef } from 'react'
import { useLanguage } from './useLanguage.js'
import { htmlCode, useSelectedVotingRegion } from './useSelectedVotingRegion.js'
import { PARTICIPATING_COUNTRIES } from '../../../shared/constants.js'
import { useOnboarded } from './useOnboarded.js'
import { persistent } from '../constants/index.js'
import { useContentQuery } from '../ctx.js'
import { useTrack } from './useTrack.js'
import { currencyDisplay } from '../utils/currencyDisplay.js'

// FIX: Is this updated?
export type VotingRegion = {
  code: string
  locale?: string
  name?: string
  currency?: string
  currencySymbol?: string
  price?: number
  maxVotes?: number
  dialCode?: number
  voteType?: string
  votingText?: string
  votingLegal?: string
  destination?: string
  formattedPrice?: string
  broadcaster?: string
  scenario?: { id: number }
  loading?: boolean
  privacyUrl?: string
  isTerritoryOf?: {
    name: string
    displayName: string
    dialCode: number
    voteType?: string
    id?: number
  }
  overseasTerritories?: {
    id: number
    name: string
  }[]
  overseaSource?: {
    name: string
    displayName: string
    dialCode: number
    voteType?: string
    id?: number
  }
}

const cache: {
  [iso: string]: Intl.DisplayNames
} = {}

export const getVotingRegionName = (
  votingRegionCode: string,
  languageCode: string,
) => {
  if (
    !votingRegionCode ||
    votingRegionCode === 'ROW' ||
    votingRegionCode === 'unknown'
  ) {
    return languageCode === 'he' ? 'שאר העולם' : 'Rest of the World'
  }
  cache[languageCode] ??= new Intl.DisplayNames([languageCode], {
    type: 'region',
  })
  const names = cache[languageCode]
  const name = names.of(votingRegionCode)
  if (name === undefined)
    console.warn('getVotingRegionName: name not found for', votingRegionCode)
  return name || '' // SAFE: empty string fallback when region name not yet loaded
}

export const useVotingRegionName = (votingRegionCode: string) => {
  const [lang] = useLanguage()
  return getVotingRegionName(votingRegionCode, lang.code)
}

export const useVotingRegion = (): [VotingRegion, (code: string) => void] => {
  const stateRef = useRef<VotingRegion | null>(null)
  const checksumRef = useRef<number | undefined>(undefined)
  const langRef = useRef<string | undefined>(undefined)

  const [lang] = useLanguage()
  const [code, setVotingRegion] = useSelectedVotingRegion()
  const [onboarded, setOnboarded] = useOnboarded()
  const { data: sourceVotingRegion = {} } = useContentQuery(
    code ? 'votingRegion' : null,
    code,
    persistent,
  )

  let votingRegionCode: string

  if (sourceVotingRegion?.isTerritoryOf?.name) {
    votingRegionCode = sourceVotingRegion?.isTerritoryOf.name
  } else if ((PARTICIPATING_COUNTRIES as readonly string[]).includes(code)) {
    votingRegionCode = code
  } else {
    votingRegionCode = 'ROW'
  }

  const {
    data = {},
    checksum,
    loading,
  } = useContentQuery(
    votingRegionCode ? 'votingRegion' : null,
    votingRegionCode,
    persistent,
  )

  if (
    checksumRef.current !== checksum ||
    langRef.current !== lang.code ||
    stateRef.current?.code !== code
  ) {
    stateRef.current = {
      code: votingRegionCode,
      locale: `${lang.code}-${code === 'ROW' ? htmlCode : code}`,

      // FIXME: There should be no defaults
      // defaults
      price: 99,
      currency: 'eur',
      maxVotes: 10,
      voteType: 'online',
      // overwrites
      ...data,
      name: getVotingRegionName(votingRegionCode, lang.code),
      loading,
    }

    if (stateRef.current.currency) {
      stateRef.current.currencySymbol = currencyDisplay(
        stateRef.current.currency,
      )
    }

    if (stateRef.current.price) {
      stateRef.current.formattedPrice = (
        stateRef.current.price / 100
      ).toLocaleString(stateRef.current.locale, {
        style: 'currency',
        currency: stateRef.current.currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    }

    if (sourceVotingRegion?.isTerritoryOf?.name) {
      stateRef.current.overseaSource = {
        name: code,
        displayName: getVotingRegionName(code, lang.code) || '',
        dialCode: sourceVotingRegion?.dialCode,
      }
    }

    langRef.current = lang.code
    checksumRef.current = checksum
  }

  useTrack(stateRef.current.code ? `region-${stateRef.current.code}` : '')

  return [
    stateRef.current,
    (newCode) => {
      setVotingRegion(newCode)
      if (onboarded && code !== newCode) {
        setOnboarded(false)
      }
    },
  ]
}
